import { ExpandMore, Info, InfoRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DELETE_PORTAL_FORM_API_URL,
  UPDATE_PORTAL_FORM_API_URL,
} from 'api/apiConstants';
import axios from 'axios';
import FormEditorSetting from 'components/FormEditorSetting';
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs';
import {
  FormDetail,
  FormDetailType,
  FormsErrorDataType,
  FormStateDataType,
  HubSpotForms,
  SelectedGateways,
} from 'containers/dashboard/Forms/FormListingType';
import { motion, Reorder } from 'framer-motion';
import usePortalId from 'hooks/portal/usePortalId';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  setAmountDetailsUpdated,
  setForm,
  setFormImageUploading,
} from 'redux/slices/formEditor';
import { dispatch } from 'redux/store';
import { MANAGE_HUBSPOT_URL, ROOT_NAVIGATION } from 'routes/paths';
import { isApiError } from 'utils/apiFunctions';
import axiosInstance from 'utils/axios';
import { toFloat, validPrice } from 'utils/formatNumber';
import { isURL } from 'utils/helper';
import FormSettingsForm from '../child/FormSettingsForm';
import DonationSettingsForm from './DonationSettingsForm';
import { accordionProps } from './elements';
import { getSelectedGateways } from './FormFunctions';
import PaymentMethods from './PaymentMethods';
import PaymentSettingsForm from './PaymentSettingsForm';
import RedirectionSettings from './RedirectionSettings';
import ThankYouPageSettings from './ThankYouPageSettings';
import TransactionCostSettings from './TransactionCostSettings';
import Emittery from 'emittery';
import StyleSettingsTab from './StyleSettingsTab';
import CouponSettingsForm from './CouponSettingsForm';
import { useAllFormCoupons } from 'api/apiHooks';
import ProductsFormEdit from './ProductsFormEdit';
import PaymentTabSettings from './PaymentTabSettings';
import EnableDeals from './EnableDeal';

declare global {
  interface Window {
    fpEventEmitter: Emittery;
    fpBroadcaster: BroadcastChannel;
  }
}

window.fpBroadcaster = new BroadcastChannel('fpEvents');
const messageReceiver = new BroadcastChannel('fpEvents');

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  '&:before': {
    display: 'none',
  },
}));

type TabsType = 'payment' | 'form' | 'styles' | 'products';

interface OwnProps {
  hubspotFormData: HubSpotForms[];
  formData: FormDetail;
}
export const currency: Record<string, string> = {
  AUD: 'Australian dollar ($)',
  GBP: 'Pound sterling (£)',
  DKK: 'Danish krone (DKK)',
  EUR: 'Euro (€)',
  HKD: 'Hong Kong dollar ($)',
  JPY: 'Japanese yen (¥)',
  NZD: 'New Zealand dollar ($)',
  NOK: 'Norwegian krone (kr)',
  ZAR: 'South African rand (R)',
  SEK: 'Swedish krona (kr)',
  CHF: 'Swiss franc (CHF)',
  USD: 'United States (US) dollar ($)',
};

const initialErrorStateData: FormsErrorDataType = {
  name: '',
  selectedHubspotFormId: '',
  mode: '',
  amount: '',
  currency: '',
  paymentType: '',
  selectedGateways: '',
  allowDiscountCode: '',
  allowDonation: '',
  allowCoupon: '',
  title: '',
  description: '',
  logo: '',
  allowTransactionCost: '',
  transactionAmount: '',
  transactionCostType: '',
  transactionCostVariableBaseAmount: '',
  transactionUnitAmount: '',
  suggestedAmount: '',
  allowRedirection: '',
  redirectUrl: '',
  transactionCostLabel: '',
  thankyouImage: '',
  thankyouTitle: '',
  thankyouSubtitle: '',
  thankyouDescription: '',
  showDonationMemory: '',
  donationMemoryLabel: '',
  donateButtonText: '',
  backButtonText: '',
  backButtonLink: '',
  allowCustomAmount: '',
  customAmount: '',
  payButtonText: '',
};

type Props = OwnProps;

const thankYouMinOneFieldVerified = (state: FormStateDataType) => {
  if (
    state.thankyouImage === '' &&
    state.thankyouTitle === '' &&
    state.thankyouSubtitle === '' &&
    state.thankyouDescription === ''
  ) {
    return false;
  }

  return true;
};

export function getBase64(file: any) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

const Form: FunctionComponent<Props> = ({ hubspotFormData, formData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const portalId = usePortalId();
  const { formId } = useParams<{ formId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const isErrorRef = useRef(false);
  const [formUpdated, setFormUpdated] = useState(0);
  const [productLoading, setProductLoading] = useState<boolean>(false);
  const [tabSelected, setTabSelected] = useState<TabsType>('form');
  const { data, isLoading } = useAllFormCoupons({
    portal_id: Number(portalId),
    formId: formId!,
  });
  const couponListData = data?.data || [];
  const { products } = useSelector((state: any) => state.productsFromEditor);

  const [state, setState] = useState<FormStateDataType>({
    name: formData.name || '',
    selectedHubspotFormId: formData.hubspot_form_id || 'select',
    mode: formData.mode === 'test' ? true : false,
    amount: formData.amount || 0,
    currency:
      formData.selected_gateways.find(method => method.is_selected)?.settings
        .payment_currency || 'USD',
    paymentType: formData.payment_type || 'single',
    selectedGateways: formData.selected_gateways,
    allowDiscountCode: formData.allow_discount_code ? true : false,
    allowCustomAmount: formData.donation_form_settings?.allow_custom_amount
      ? true
      : false,
    customAmount: formData.donation_form_settings?.amount || 0,
    suggestedAmount: formData?.donation_form_settings?.suggested_amount || [],
    allowDonation: formData?.donation_form_settings?.allow_donation
      ? true
      : false,
    allowCoupon: formData?.allow_coupons ? true : false || false,
    title: formData?.donation_form_settings?.donation_form_title || '',
    description:
      formData?.donation_form_settings?.donation_form_description || '',
    logo: formData?.donation_form_settings?.donation_form_image || '',
    allowTransactionCost:
      formData?.donation_form_settings?.transaction_cost_setting
        ?.allow_transaction_cost || false,
    transactionAmount:
      formData?.donation_form_settings?.transaction_cost_setting
        ?.transaction_cost_fixed_amount || 0,
    transactionCostType:
      formData?.donation_form_settings?.transaction_cost_setting
        ?.transaction_cost_type || 'fixed',
    transactionCostVariableBaseAmount:
      formData?.donation_form_settings?.transaction_cost_setting
        ?.transaction_cost_variable_base_amount || 0,
    transactionUnitAmount:
      formData?.donation_form_settings?.transaction_cost_setting
        ?.transaction_cost_variable_unit_amount || 0,
    allowRedirection:
      formData?.after_submission_settings?.allow_redirection || false,
    redirectUrl: formData?.after_submission_settings?.redirect_url || '',
    transactionCostLabel:
      formData?.donation_form_settings?.transaction_cost_setting
        ?.transaction_cost_label || '',
    thankyouImage: formData?.after_submission_settings?.thankyou_image || '',
    thankyouTitle: formData?.after_submission_settings?.thankyou_title || '',
    thankyouSubtitle:
      formData?.after_submission_settings?.thankyou_subtitle || '',
    thankyouDescription:
      formData?.after_submission_settings?.thankyou_description || '',
    showDonationMemory:
      formData?.donation_form_settings?.show_donation_memory || true,
    donationMemoryLabel:
      formData?.donation_form_settings?.donation_memory_label || '',

    donateButtonText:
      formData?.donation_form_settings?.donate_button_text || '',
    backButtonText: formData?.after_submission_settings?.back_button_text || '',

    themeColor: formData?.theme_color || '',
    backButtonLink: formData?.after_submission_settings.back_button_link || '',
    payButton: formData?.payButton || false,
    payButtonText: formData?.pay_button_text,
    defaultGateway: formData?.default_gateway,
    allowRecurringForDonation: formData?.donation_form_settings
      ?.allowRecurringForDonation
      ? true
      : false,
    allowOnlyRecurringPayments: formData?.donation_form_settings
      ?.allowOnlyRecurringPayments
      ? true
      : false,
    allowSubscription: formData?.allowSubscription ? true : false,
    allowCustomProduct: formData?.allow_custom_product ? true : false,
    useStripeCheckoutSession: formData?.useStripeCheckoutSession
      ? true
      : false || false,
    enableStripeWallet: formData?.enableStripeWallet ? true : false,
    selectSingleProduct: formData?.select_single_product ? true : false,
    showProductDropDown: formData.showProductDropDown ? true : false,
  });

  const {
    formDetails,
    subscription: isSubscriptionEnabled,
    razorpay_plan_id,
    razorpay_plan_amount,
    updateFormAmount,
  } = useSelector((state: any) => state.formEditor);

  const validateForm = (
    validateFormData: FormStateDataType,
    t: TFunction<'translation', undefined>,
    getSelectedGateways: (selectedGateways: SelectedGateways[]) => string[]
  ) => {
    const validate = {
      name: (name: string) => (name.length > 0 ? true : t('name_is_required')),
      selectedHubspotFormId: (selectedHubspotFormId: string) =>
        selectedHubspotFormId !== 'select'
          ? true
          : t('hubspot_form_is_required'),
      mode: (mode: boolean) =>
        mode === true || mode === false ? true : t('mode_is_required'),
      themeColor: (themeColor: string) =>
        themeColor.length > 0 ? true : t('theme_color_is_required'),
      amount: (amount: string) =>
        formData?.products?.length || products?.length !== 0
          ? true
          : !validateFormData.allowCustomAmount
          ? validPrice(parseFloat(amount)) && parseFloat(amount) > 0
            ? true
            : t('fill_valid_amount')
          : true,

      paymentType: (paymentType: string) =>
        paymentType.length > 0 ? true : t('payment_type_is_required'),
      selectedGateways: (selectedGateways: SelectedGateways[]) =>
        getSelectedGateways(selectedGateways).length > 0
          ? true
          : t('select_atleast_one_gateway'),
      allowDiscountCode: (allowDiscountCode: boolean) => true,
      allowDonation: (allowDonation: boolean) =>
        allowDonation === true || allowDonation === false
          ? true
          : t('allow_donation') + '' + t('is_required'),
      title: (title: string) =>
        validateFormData.allowDonation
          ? title.length
            ? true
            : t('title') + ' ' + t('is_required')
          : true,
      description: (description: string) =>
        validateFormData.allowDonation
          ? description.length
            ? true
            : t('description') + ' ' + t('is_required')
          : true,
      logo: (logo: string) =>
        validateFormData.allowDonation
          ? logo.length
            ? true
            : t('logo') + ' ' + t('is_required')
          : true,

      allowTransactionCost: (allowTransactionCost: boolean) =>
        validateFormData.allowDonation
          ? allowTransactionCost === true || allowTransactionCost === false
            ? true
            : t('allow_transaction_cost') + ' ' + t('is_required')
          : true,
      transactionAmount: (transactionAmount: number) =>
        !validateFormData.allowDonation ||
        !validateFormData.allowTransactionCost ||
        validateFormData.transactionCostType === 'variable'
          ? true
          : transactionAmount > 0
          ? true
          : t('transaction_amount') + ' ' + t('should_be_greater_than') + ' 0',
      transactionCostType: (transactionCostType: string) => true,
      transactionCostVariableBaseAmount: (
        transactionCostVariableBaseAmount: number
      ) =>
        !validateFormData.allowDonation ||
        !validateFormData.allowTransactionCost ||
        validateFormData.transactionCostType === 'fixed'
          ? true
          : transactionCostVariableBaseAmount > 0
          ? true
          : t('transaction_cost_variable_base_amount') +
            ' ' +
            t('should_be_greater_than') +
            ' 0',
      transactionUnitAmount: (transactionUnitAmount: number) =>
        !validateFormData.allowDonation ||
        !validateFormData.allowTransactionCost ||
        validateFormData.transactionCostType === 'fixed'
          ? true
          : transactionUnitAmount > 0
          ? true
          : t('transaction_unit_amount') +
            ' ' +
            t('should_be_greater_than') +
            ' 0',
      allowRedirection: (allowRedirection: boolean) =>
        allowRedirection === true || allowRedirection === false
          ? true
          : t('allow_redirection') + ' ' + t('is_required'),
      redirectUrl: (redirectUrl: string) =>
        validateFormData.allowRedirection === false
          ? true
          : isURL(redirectUrl)
          ? true
          : t('valid_url') + ' ' + t('is_required'),
      backButtonLink: (backButtonLink: string) =>
        validateFormData.allowRedirection === true
          ? true
          : isURL(backButtonLink)
          ? true
          : t('back_button_link') + ' ' + t('is_required'),
      transactionCostLabel: (transactionCostLabel: string) =>
        !validateFormData.allowDonation ||
        !validateFormData.allowTransactionCost
          ? true
          : transactionCostLabel.length < 1
          ? t('transaction_cost_label') + ' ' + t('is_required')
          : true,
      thankyouImage: (thankyouImage: string) => {
        if (validateFormData.allowRedirection) {
          return true;
        }

        if (thankYouMinOneFieldVerified(validateFormData)) {
          return true;
        }

        return thankyouImage.length
          ? true
          : t('thank_you_image') + ' ' + t('is_required');
      },

      thankyouTitle: (thankyouTitle: string) => {
        if (validateFormData.allowRedirection) {
          return true;
        }

        if (thankYouMinOneFieldVerified(validateFormData)) {
          return true;
        }

        return thankyouTitle.length
          ? true
          : t('thank_you_title') + ' ' + t('is_required');
      },
      thankyouSubtitle: (thankyouSubtitle: string) => {
        if (validateFormData.allowRedirection) {
          return true;
        }

        if (thankYouMinOneFieldVerified(validateFormData)) {
          return true;
        }

        return thankyouSubtitle.length
          ? true
          : t('thank_you_subtitle') + ' ' + t('is_required');
      },
      thankyouDescription: (thankyouDescription: string) => {
        if (validateFormData.allowRedirection) {
          return true;
        }

        if (thankYouMinOneFieldVerified(validateFormData)) {
          return true;
        }

        return thankyouDescription.length
          ? true
          : t('thank_you_description') + ' ' + t('is_required');
      },
      showDonationMemory: (showDonationMemory: boolean) =>
        !validateFormData.allowDonation
          ? true
          : showDonationMemory === true || showDonationMemory === false
          ? true
          : t('show_donation_memory') + ' ' + t('is_required'),
      donationMemoryLabel: (donationMemoryLabel: string) =>
        !validateFormData.allowDonation || !validateFormData.showDonationMemory
          ? true
          : donationMemoryLabel.length < 1
          ? t('donation_memory_label') + ' ' + t('is_required')
          : true,
      donateButtonText: (donateButtonText: string) =>
        !validateFormData.allowDonation
          ? true
          : donateButtonText.length < 1
          ? t('donate_button_text') + ' ' + t('is_required')
          : true,
      backButtonText: (backButtonText: string) =>
        validateFormData.allowRedirection === true
          ? true
          : backButtonText.length < 1
          ? t('back_button_text') + ' ' + t('is_required')
          : true,

      customAmount: (customAmount: string) =>
        !validateFormData.allowDonation
          ? true
          : validateFormData.allowCustomAmount
          ? validPrice(parseFloat(customAmount)) && parseFloat(customAmount) > 0
            ? true
            : t('allow_custom_amount') + ' ' + t('is_required')
          : true,
      payButtonText: (payButtonText: string) => {
        return validateFormData.payButton === true
          ? payButtonText.length > 0
            ? true
            : t('pay_button_text_required')
          : true;
      },
    };

    const errObj = {
      ...initialErrorStateData,
    };
    let errorFieldsVar = false;
    _.forIn(validateFormData, (value, key) => {
      if (typeof validate[key as keyof typeof validate] === 'function') {
        const error = validate[key as keyof typeof validate](value as never);
        if (error !== true) {
          errorFieldsVar = true;
        }
        errObj[key as keyof FormsErrorDataType] = (error as string) ?? '';
      }
    });

    return { errorFieldsVar, errObj };
  };

  useEffect(() => {
    dispatch(
      setForm({
        formId,

        ...state,
      })
    );
  }, [state, formId]);

  useEffect(() => {
    // disable donations automatically if the subscription is enabled
    if (isSubscriptionEnabled) {
      setState(state => ({
        ...state,
        allowDonation: false,
      }));
    }
  }, [isSubscriptionEnabled]);

  const [submitting, setSubmitting] = useState(false);

  const [stateError, setStateError] = useState(initialErrorStateData);

  const onChangeElement = React.useCallback(
    (
      event:
        | React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
          >
        | SelectChangeEvent
    ) => {
      const { name, value } = event.target;
      setState({ ...state, [name]: value });
    },
    [state]
  );

  const onChangeMode = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = event.target;
      setState({ ...state, [name]: checked });
    },
    [state]
  );

  const changeKeyValue = (name: string, value: boolean) => {
    setState({ ...state, [name]: value });
  };

  const handleDrop = React.useCallback(
    async (
      e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
      name: string
    ) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      if (e.target.files) {
        //@ts-ignore
        const base64Data = await getBase64(e.target.files[0]);
        dispatch(
          setFormImageUploading({
            formImageTypeUploading: name,
          })
        );

        try {
          const result = await axiosInstance.post(
            '/formpayhome/portal/imageUploadS3',
            {
              imageType: name,
              formId: formId,
              base64_imageString: base64Data,
            }
          );

          if (result.data.success) {
            setState(state => ({
              ...state,
              [name]: result.data.image_url as string,
            }));
          }
        } catch (err) {
          if (axios.isAxiosError(err)) {
            enqueueSnackbar(err.message, { variant: 'error' });
          }
        } finally {
          dispatch(
            setFormImageUploading({
              formImageTypeUploading: null,
            })
          );
        }
      }
    },
    [state]
  );

  const onChangeGatewayCheck = React.useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement>,
      checked: boolean,
      gatewayId: string
    ) => {
      const copy: SelectedGateways[] = _.cloneDeep([...state.selectedGateways]);
      const index = _.findIndex(copy, function (o) {
        return o._id === gatewayId;
      });

      const checkedStatus = (event.target as HTMLInputElement).checked;
      let tempSelectedDefaultGateway = '';
      if (checkedStatus) {
        copy[index].is_selected = true;
        // tempSelectedDefaultGateway = copy[index].
      } else {
        copy[index].is_selected = false;
      }

      copy.map(item => {
        if (item.is_selected) {
          tempSelectedDefaultGateway = item.name;
        }
      });
      setState({
        ...state,
        selectedGateways: [...copy],
        defaultGateway: tempSelectedDefaultGateway,
        // defaultGateway: ,
      });
    },
    [state]
  );

  const deleteForm = async () => {
    try {
      const response = await axiosInstance.post(
        `${DELETE_PORTAL_FORM_API_URL}${formId}`
      );
      enqueueSnackbar(response.data.message, { variant: 'success' });
      navigate(ROOT_NAVIGATION.general.forms(portalId));
      // refetch();
    } catch (res) {
      if (axios.isAxiosError(res)) {
        enqueueSnackbar(res.message, { variant: 'error' });
      }
    }
  };

  const [publishFormData, setPublishFormData] = useState<FormDetailType>();
  const updateForm = async () => {
    const { errObj, errorFieldsVar } = validateForm(
      state,
      t,
      getSelectedGateways
    );
    isErrorRef.current = errorFieldsVar;
    setStateError({ ...stateError, ...errObj });

    if (isErrorRef.current) {
      enqueueSnackbar(t('fill_the_required_fields'), { variant: 'error' });
      setTimeout(errorFieldFocus, 100);

      return false;
    }
    try {
      setSubmitting(true);

      const response = await axiosInstance.post(
        `${UPDATE_PORTAL_FORM_API_URL}/${formId}`,
        {
          name: state.name,
          form_type: 'normal',
          portal_id: Number(portalId),
          allow_coupons: state.allowCoupon,
          hubspot_form_id: state.selectedHubspotFormId,
          mode: state.mode ? 'test' : 'live',
          amount:
            state.allowDonation && state.allowCustomAmount
              ? toFloat(state.customAmount)
              : toFloat(state.amount),
          currency: state.selectedGateways.find(method => method.is_selected)
            ?.settings.payment_currency,
          payment_type: state.paymentType,
          allow_discount_code: state.allowDiscountCode,
          donation_form_settings: {
            allow_donation: state.allowDonation,
            allowRecurringForDonation: state.allowRecurringForDonation,
            allowOnlyRecurringPayments: state.allowOnlyRecurringPayments,
            donation_form_title: state.allowDonation ? state.title : '',
            donation_form_description: state.allowDonation
              ? state.description
              : '',
            donation_form_image: state.allowDonation ? state.logo : '',
            allow_custom_amount: state.allowDonation
              ? state.allowCustomAmount
              : false,
            amount:
              state.allowDonation && state.allowCustomAmount
                ? state.customAmount
                : 0,
            suggested_amount:
              state.allowDonation && state.allowCustomAmount
                ? state.suggestedAmount
                : [],
            show_donation_memory: state.allowDonation
              ? state.showDonationMemory
              : false,
            donation_memory_label:
              state.allowDonation && state.showDonationMemory
                ? state.donationMemoryLabel
                : '',

            donate_button_text: state.donateButtonText,
            transaction_cost_setting: {
              allow_transaction_cost: state.allowTransactionCost,
              transaction_cost_label: state.allowTransactionCost
                ? state.transactionCostLabel
                : '',
              transaction_cost_type: state.allowTransactionCost
                ? state.transactionCostType
                : 'fixed',

              transaction_cost_fixed_amount:
                state.allowTransactionCost &&
                state.transactionCostType === 'fixed'
                  ? state.transactionAmount
                  : 0,
              transaction_cost_variable_base_amount:
                state.allowTransactionCost &&
                state.transactionCostType === 'variable'
                  ? state.transactionCostVariableBaseAmount
                  : 0,
              transaction_cost_variable_unit_amount:
                state.allowTransactionCost &&
                state.transactionCostType === 'variable'
                  ? state.transactionUnitAmount
                  : 0,
            },
          },
          after_submission_settings: {
            back_button_text: state.backButtonText,
            allow_redirection: state.allowRedirection,
            redirect_url: state.redirectUrl,
            thankyou_image: state.thankyouImage,
            thankyou_title: state.thankyouTitle,
            thankyou_subtitle: state.thankyouSubtitle,
            thankyou_description: state.thankyouDescription,
            back_button_link: state.backButtonLink,
          },
          allow_redirection: state.allowRedirection,
          redirect_url: state.redirectUrl,
          selected_gateways: getSelectedGateways(state.selectedGateways),
          theme_color: state.themeColor,
          payButton: state.payButton,
          pay_button_text: state.payButtonText,
          default_gateway: state.defaultGateway,
          allowSubscription: state.allowRecurringForDonation,
          useStripeCheckoutSession: state.useStripeCheckoutSession,
          enableStripeWallet: state.enableStripeWallet,
          allow_custom_product: state.allowCustomProduct,
          select_single_product: state.selectSingleProduct,
          show_product_dropdown: state.showProductDropDown,
        }
      );
      if (response.data.success) {
        enqueueSnackbar(response.data.message, { variant: 'success' });
        setPublishFormData(response.data);
      }
    } catch (error) {
      if (isApiError(error)) {
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSubscriptionEnabled && razorpay_plan_id && state.amount > 0) {
      updateForm();
    }
  }, [state.amount, isSubscriptionEnabled, razorpay_plan_id]);

  useEffect(() => {
    if (razorpay_plan_id && razorpay_plan_id !== '') {
      setState(state => ({
        ...state,
        amount: parseFloat((razorpay_plan_amount / 100).toFixed(2)),
      }));
    }
  }, [razorpay_plan_id]);

  useEffect(() => {
    const updateFormProperties = (event: any) => {
      const propertyName = event.data.propertyName;

      const value = event.data.value;
      axiosInstance.post(
        '/formpayhome/portal/updatePortalFormSettings/' + formId,
        {
          form_id: formId,
          [propertyName]: value,
        }
      );
    };
    messageReceiver.onmessage = updateFormProperties;
  }, []);

  useEffect(() => {
    // so that form is updated only once
    if (
      isSubscriptionEnabled &&
      updateFormAmount &&
      state.amount === razorpay_plan_amount
    ) {
      updateForm();
      dispatch(setAmountDetailsUpdated(false));
    }
  }, [state.amount, updateFormAmount]);

  const formRef = useRef<HTMLDivElement | null>(null);

  const errorFieldFocus = React.useCallback(() => {
    if (formRef.current) {
      const errorDesEl = formRef.current.querySelectorAll('.Mui-error');

      const firstError = Array.from(errorDesEl).find(el => el.innerHTML !== '');

      if (firstError) {
        firstError.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'center',
        });

        if (
          !firstError
            .closest('.MuiAccordion-root')
            ?.classList.contains('Mui-expanded')
        ) {
          (
            firstError.closest('.MuiAccordion-root')
              ?.firstChild as HTMLDivElement
          ).click();
        }
      }
    }
  }, []);

  useEffect(() => {
    if (publishFormData) {
      setFormUpdated(value => value + 1);
    }
  }, [publishFormData]);

  const showPreview =
    (publishFormData && formUpdated !== 0) ||
    (state.selectedHubspotFormId && formData.hubspot_form_id !== '',
    formData.hubspot_form_id !== '');

  //@ts-ignore
  window.showPreviewForForm = showPreview;

  const onChangeSuggestion = React.useCallback(
    (name: string, value: any[]) => {
      setState({ ...state, [name]: value });
    },
    [state]
  );

  const links = React.useMemo(
    () => [
      {
        name: t('dashboard'),
        href: ROOT_NAVIGATION.general.home(portalId),
      },
      { name: t('forms'), href: ROOT_NAVIGATION.general.forms(portalId) },
      { name: t('edit'), href: 'edit' },
    ],
    [portalId, t]
  );

  const subscriptionEnabled = formDetails?.subscription;
  const selectableDefaultGateways: Array<{
    name: string;
    label: string;
  }> = [];

  state.selectedGateways.forEach(gateway => {
    if (gateway.is_selected && gateway.can_be_default) {
      selectableDefaultGateways.push({
        name: gateway.name,
        label: gateway.label,
      });
    }
  });

  const formSettings = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className='flex flex-col gap-4'
    >
      <Accordion defaultExpanded square={false} sx={accordionProps}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography
            sx={{ marginBottom: '5px' }}
            variant='subtitle2'
            color='text secondary'
          >
            {t('form_settings')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            className='mb-5'
            control={
              <Switch
                name='mode'
                checked={state.mode}
                onChange={onChangeMode}
              />
            }
            label={t('enable_test_mode')}
          />
          <FormSettingsForm
            name={state.name}
            selectedHubspotFormId={state.selectedHubspotFormId}
            hubspotForms={hubspotFormData}
            onChangeElement={onChangeElement}
            stateError={stateError}
          />
          <Link
            target='_blank'
            className='manage-hs-form-link'
            href={MANAGE_HUBSPOT_URL(portalId)}
            style={{ fontSize: 'x-small' }}
          >
            {t('manage_hubspot_form')}
          </Link>

          <div className='mt-2 flex items-center text-[14px] text-gray-600'>
            <Info sx={{ width: '15px', height: '15px' }} />{' '}
            <span className='ml-1'> {t('email_captcha_mandatory')}</span>
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded square={false} sx={accordionProps}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography
            sx={{ marginBottom: '5px' }}
            variant='subtitle2'
            color='text secondary'
          >
            {t('donation_form_settings')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <DonationSettingsForm
            donateButtonText={state.donateButtonText}
            showDonationMemory={state.showDonationMemory}
            allowDonation={state.allowDonation}
            allowRecurringForDonation={state.allowRecurringForDonation}
            allowOnlyRecurringPayments={state.allowOnlyRecurringPayments}
            donationMemoryLabel={state.donationMemoryLabel}
            title={state.title}
            description={state.description}
            logo={state.logo}
            onChangeElement={onChangeElement}
            onChangeMode={onChangeMode}
            handleDrop={handleDrop}
            stateError={stateError}
            methods={state.selectedGateways}
            changeKeyValue={changeKeyValue}
            allowSubscription={state.allowSubscription}
            allowCustomProduct={state.allowCustomProduct}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded square={false} sx={accordionProps}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography
            sx={{ marginBottom: '5px' }}
            variant='subtitle2'
            color='text secondary'
          >
            {t('coupon_settings')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CouponSettingsForm
            allowDonation={state.allowDonation}
            allowCoupon={state.allowCoupon}
            onChangeMode={onChangeMode}
            data={couponListData}
            isLoading={isLoading}
            changeKeyValue={changeKeyValue}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded square={false} sx={accordionProps}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography
            sx={{ marginBottom: '5px' }}
            variant='subtitle2'
            color='text secondary'
          >
            {t('payment_settings')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PaymentSettingsForm
            currencies={currency}
            defaultGateway={state.defaultGateway}
            selectedMode={state.mode}
            selectedCurrency={state.currency}
            allowDonation={state.allowDonation}
            selectedPaymentType={state.paymentType}
            amount={state.amount}
            selectableDefaultGateways={selectableDefaultGateways}
            onChangeSuggestion={onChangeSuggestion}
            suggestedAmount={state.suggestedAmount!}
            onChangeElement={onChangeElement}
            onChangeMode={onChangeMode}
            stateError={stateError}
            allowCustomAmount={state.allowCustomAmount}
            customAmount={state.customAmount}
            subscriptionEnabled={subscriptionEnabled}
            setTabSelected={setTabSelected}
            formData={formData}
          />
        </AccordionDetails>
      </Accordion>
      {state.allowDonation ? (
        <Accordion
          defaultExpanded
          square={false}
          sx={{
            padding: '0px 10px',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: '16px !important',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography
              sx={{ marginBottom: '5px' }}
              variant='subtitle2'
              color='text secondary'
            >
              {t('transaction_cost_settings')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TransactionCostSettings
              onChangeElement={onChangeElement}
              onChangeMode={onChangeMode}
              stateError={stateError}
              transactionCostLabel={state.transactionCostLabel}
              allowTransactionCost={state.allowTransactionCost}
              transactionAmount={state.transactionAmount}
              transactionCostType={state.transactionCostType}
              transactionUnitAmount={state.transactionUnitAmount}
              transactionCostVariableBaseAmount={
                state.transactionCostVariableBaseAmount
              }
            />
          </AccordionDetails>
        </Accordion>
      ) : null}
      <Accordion defaultExpanded square={false} sx={accordionProps}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography
            sx={{ marginBottom: '5px' }}
            variant='subtitle2'
            color='text secondary'
          >
            {t('redirection_setting')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RedirectionSettings
            onChangeElement={onChangeElement}
            onChangeMode={onChangeMode}
            stateError={stateError}
            allowRedirection={state.allowRedirection}
            redirectUrl={state.redirectUrl}
          />
        </AccordionDetails>
      </Accordion>
      {!state.allowRedirection ? (
        <Accordion
          defaultExpanded
          square={false}
          sx={{
            padding: '0px 10px',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: '16px !important',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography
              sx={{ marginBottom: '5px' }}
              variant='subtitle2'
              color='text secondary'
            >
              {t('thank_you_page_settings')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ThankYouPageSettings
              handleDrop={handleDrop}
              thankyouTitle={state.thankyouTitle}
              thankyouSubtitle={state.thankyouSubtitle}
              thankyouDescription={state.thankyouDescription}
              thankyouImage={state.thankyouImage}
              onChangeElement={onChangeElement}
              onChangeMode={onChangeMode}
              backButtonText={state.backButtonText}
              stateError={stateError}
              backButtonLink={state.backButtonLink}
            />
          </AccordionDetails>
        </Accordion>
      ) : null}
      <Accordion defaultExpanded square={false} sx={accordionProps}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography
            sx={{ marginBottom: '5px' }}
            variant='subtitle2'
            color='text secondary'
          >
            {t('payment_method')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PaymentMethods
            methods={state.selectedGateways}
            mode={state.mode}
            onChangeGatewayCheck={onChangeGatewayCheck}
            stateError={stateError}
            allowRecurringForDonation={state.allowRecurringForDonation}
          />
          {selectableDefaultGateways.length > 0 ? (
            <div className='flex mt-5'>
              <FormControl fullWidth>
                <InputLabel id='defaultGateway'>
                  {t('default_gateway')}
                </InputLabel>
                <Select
                  id='defaultGateway'
                  value={state.defaultGateway}
                  label={t('select_default_gateway')}
                  variant='outlined'
                  name='defaultGateway'
                  onChange={onChangeElement}
                >
                  {selectableDefaultGateways.map((gateway, index) => {
                    return (
                      <MenuItem key={gateway.name} value={gateway.name}>
                        {gateway.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Tooltip title={t('default_gateway_tooltip_description')}>
                <IconButton>
                  <InfoRounded />
                </IconButton>
              </Tooltip>
            </div>
          ) : null}
        </AccordionDetails>
      </Accordion>
      <Stack spacing={2} direction='row' sx={{ marginTop: '50px' }}>
        <LoadingButton
          variant='contained'
          onClick={updateForm}
          loading={submitting}
        >
          {!showPreview ? t('publish_form') : t('update_form')}
        </LoadingButton>
        <Button onClick={deleteForm} variant='text'>
          {t('delete_form')}
        </Button>
      </Stack>{' '}
    </motion.div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ maxWidth: '98%', margin: '0 auto' }}
      ref={formRef}
    >
      <div className='flex justify-between items-start'>
        <HeaderBreadcrumbs heading={t('forms')} links={links} />
        <EnableDeals
          InitalenableDeal={
            // @ts-ignore
            (formData.enable_deal as boolean) || false
          }
          publishFormData={publishFormData}
          setPublishFormData={setPublishFormData}
          state={state}
          formUpdated={formUpdated}
          formData={formData}
          setFormUpdated={setFormUpdated}
        />
      </div>
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}
      >
        <Stack direction='column' spacing='10px' sx={{ minWidth: '50%' }}>
          <div className='flex'>
            <div
              className={`py-2 px-5 cursor-pointer `}
              style={{
                ...(tabSelected === 'form' && {
                  borderBottom: `2px solid ${theme.palette.primary.main} `,
                  color: ` ${theme.palette.primary.main}`,
                }),
              }}
              onClick={() => setTabSelected('form')}
            >
              {t('form')}
            </div>
            <div
              className={`py-2 px-5 cursor-pointer `}
              style={{
                ...(tabSelected === 'payment' && {
                  borderBottom: `2px solid ${theme.palette.primary.main} `,
                  color: ` ${theme.palette.primary.main}`,
                }),
              }}
              onClick={() => setTabSelected('payment')}
            >
              {t('payment')}
            </div>
            <div
              className={`py-2 px-5 cursor-pointer `}
              style={{
                ...(tabSelected === 'styles' && {
                  borderBottom: `2px solid ${theme.palette.primary.main} `,
                  color: ` ${theme.palette.primary.main}`,
                }),
              }}
              onClick={() => setTabSelected('styles')}
            >
              {t('styles')}
            </div>
            {productLoading ? (
              <div className='flex justify-between items-center'>
                <div
                  className={`py-2 px-5 cursor-pointer `}
                  style={{
                    ...(tabSelected === 'products' && {
                      borderBottom: `2px solid ${theme.palette.primary.main} `,
                      color: ` ${theme.palette.primary.main}`,
                    }),
                  }}
                  onClick={() => setTabSelected('products')}
                >
                  {t('products')}
                </div>
                <CircularProgress size={20} />
              </div>
            ) : (
              <>
                <div
                  className={`py-2 px-5 cursor-pointer `}
                  style={{
                    ...(tabSelected === 'products' && {
                      borderBottom: `2px solid ${theme.palette.primary.main} `,
                      color: ` ${theme.palette.primary.main}`,
                    }),
                  }}
                  onClick={() => {
                    if (!state.allowDonation) {
                      setTabSelected('products');
                    }
                  }}
                >
                  {state.allowDonation ? (
                    <Tooltip
                      title='To add the products, disable the donation toggle'
                      arrow
                    >
                      <span>{t('products')}</span>
                    </Tooltip>
                  ) : (
                    t('products')
                  )}
                </div>
              </>
            )}
          </div>
          {tabSelected === 'form' && formSettings}
          {tabSelected === 'payment' && (
            <PaymentTabSettings
              selectedGateways={state.selectedGateways}
              allowDonation={state.allowDonation}
              onChangeMode={onChangeMode}
              allowSubscription={state.allowSubscription}
              changeKeyValue={changeKeyValue}
              allowRecurringForDonation={state.allowRecurringForDonation}
              state={state}
              setPublishFormData={setPublishFormData}
              publishFormData={publishFormData}
              setProductLoading={setProductLoading}
              productLoading={productLoading}
              setFormUpdated={setFormUpdated}
              formUpdated={formUpdated}
              formData={formData}
              stripeOptionInitial={state.useStripeCheckoutSession}
              ewalletStripeInitial={state.enableStripeWallet}
            />
          )}
          {tabSelected === 'styles' && (
            <StyleSettingsTab
              state={state}
              stateError={stateError}
              onChangeMode={onChangeMode}
              onChangeElement={onChangeElement}
            />
          )}

          {tabSelected === 'products' && (
            <ProductsFormEdit
              formId={formId}
              state={state}
              formData={formData}
              productDropdownInitial={
                // @ts-ignore
                (formData.show_product_dropdown as boolean) || false
              }
              setPublishFormData={setPublishFormData}
              publishFormData={publishFormData}
              setProductLoading={setProductLoading}
              productLoading={productLoading}
              setFormUpdated={setFormUpdated}
              formUpdated={formUpdated}
              allowCustomProduct={state.allowCustomProduct}
              onChangeMode={onChangeMode}
              selectSingleProduct={state.selectSingleProduct}
              changeKeyValue={changeKeyValue}
            />
          )}
        </Stack>
        {showPreview ? (
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          <FormEditorSetting formId={formId!} formUpdated={formUpdated} />
        ) : (
          <div className='publish-form-note'>
            <div className='text-center'>
              {t('publish_the_form_to_load_preview')}{' '}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default React.memo(Form);
